import { memo, type CSSProperties } from 'react'
import type { ExerciseId, FigurePose } from '@/domain/exercise'
import { getFigure } from '@/data/figures'
import { cx } from '@/lib/cx'
import styles from './Figure.module.css'

interface FigureProps {
  exerciseId: ExerciseId
  /** Dibuja la figura en terracota: se usa en el ejercicio en curso. */
  accent?: boolean
}

/** Lo que tarda cada pose en pantalla. Más rápido no se lee; más lento se arrastra. */
const FRAME_SECONDS = 0.5

const points = (line: readonly number[]): string => {
  const pairs: string[] = []
  for (let i = 0; i < line.length; i += 2) pairs.push(`${line[i]},${line[i + 1]}`)
  return pairs.join(' ')
}

function Pose({ pose }: { pose: FigurePose }) {
  return (
    <>
      <circle className={styles.head} cx={pose.head[0]} cy={pose.head[1]} r={7} />
      {pose.lines.map((line, index) => (
        <polyline key={index} className={styles.limb} points={points(line)} />
      ))}
    </>
  )
}

/**
 * Monigote animado del ejercicio.
 *
 * Las poses se dibujan una al lado de otra en una tira y el SVG solo enseña la
 * casilla de 100x100: la animación desplaza la tira de pose en pose. Así el
 * mismo mecanismo sirve para dos fotogramas o para seis, y un movimiento largo
 * (la oruga, el burpee) se puede contar entero.
 */
export const Figure = memo(function Figure({ exerciseId, accent = false }: FigureProps) {
  const frames = getFigure(exerciseId)
  if (!frames || frames.length === 0) return null

  const moving = frames.length > 1
  const strip: CSSProperties | undefined = moving
    ? ({
        '--frames': frames.length,
        '--shift': `${-100 * frames.length}px`,
        animationDuration: `${(frames.length * FRAME_SECONDS).toFixed(2)}s`,
        animationTimingFunction: `steps(${frames.length})`,
      } as CSSProperties)
    : undefined

  return (
    <svg className={cx(styles.figure, accent && styles.accent)} viewBox="0 0 100 100" aria-hidden="true">
      <g className={cx(moving && styles.strip)} style={strip}>
        {frames.map((pose, index) => (
          <g key={index} transform={`translate(${index * 100} 0)`}>
            <Pose pose={pose} />
          </g>
        ))}
      </g>
    </svg>
  )
})
